import { ChevronLeft, ChevronRight } from "lucide-react";

type ClePaginationProps = {
  ariaLabel: string;
  isDisabled?: boolean;
  itemLabel: string;
  onPageChange: (page: number) => void;
  page: number;
  total: number;
  totalPages: number;
};

const MAX_VISIBLE_PAGES = 5;

const getVisiblePages = (page: number, totalPages: number): number[] => {
  const visibleCount = Math.min(totalPages, MAX_VISIBLE_PAGES);
  const halfWindow = Math.floor(visibleCount / 2);
  const startPage = Math.min(
    Math.max(page - halfWindow, 1),
    Math.max(totalPages - visibleCount + 1, 1),
  );

  return Array.from({ length: visibleCount }, (_, index) => startPage + index);
};

const ClePagination = ({
  ariaLabel,
  isDisabled,
  itemLabel,
  onPageChange,
  page,
  total,
  totalPages,
}: ClePaginationProps) => {
  const visiblePages = getVisiblePages(page, totalPages);

  return (
    <div className="cle-pagination">
      <p className="cle-pagination__summary">
        Side {page} af {totalPages}{" "}
        <span className="opacity-50">
          ({total} {itemLabel})
        </span>
      </p>
      <nav className="cle-pagination__nav" aria-label={ariaLabel}>
        <ul className="cle-pagination__list">
          <li
            className={`cle-pagination__item ${
              page <= 1 || isDisabled ? "cle-pagination__item--disabled" : ""
            }`}
          >
            <button
              type="button"
              className="cle-pagination__button cle-pagination__button--direction"
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1 || isDisabled}
            >
              <ChevronLeft size={16} aria-hidden="true" />
              Forrige
            </button>
          </li>
          {visiblePages.map((visiblePage) => (
            <li
              key={visiblePage}
              className={`cle-pagination__item ${
                visiblePage === page ? "cle-pagination__item--active" : ""
              }`}
              aria-current={visiblePage === page ? "page" : undefined}
            >
              <button
                type="button"
                className="cle-pagination__button"
                onClick={() => onPageChange(visiblePage)}
                disabled={visiblePage === page || isDisabled}
              >
                {visiblePage}
              </button>
            </li>
          ))}
          <li
            className={`cle-pagination__item ${
              page >= totalPages || isDisabled
                ? "cle-pagination__item--disabled"
                : ""
            }`}
          >
            <button
              type="button"
              className="cle-pagination__button cle-pagination__button--direction"
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages || isDisabled}
            >
              Næste
              <ChevronRight size={16} aria-hidden="true" />
            </button>
          </li>
        </ul>
      </nav>
    </div>
  );
};

export default ClePagination;
